import React, { useCallback, useEffect, useState } from 'react';
import { Bell, X, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useProject } from '../context/ProjectContext';
import { useDataVersion } from '../context/DataVersionContext';
import { formatBRL, formatDateBR } from '../lib/format';

const LEVEL_META = {
  overdue: { label: 'Vencida', tone: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30' },
  today: { label: 'Vence hoje', tone: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30' },
  '1day': { label: 'Vence amanhã', tone: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30' },
  '5day': { label: 'Vence em breve', tone: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' },
};

export default function NotificationBell() {
  const { currentProject } = useProject();
  const { version } = useDataVersion();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!currentProject) return;
    try {
      const { data } = await api.get(`/projects/${currentProject.id}/notifications`);
      setItems(data);
    } catch (_) { /* silent */ }
  }, [currentProject]);

  useEffect(() => { load(); }, [load, version]);

  if (!currentProject) return null;
  const count = items.length;

  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="notif-bell"
        aria-label={`${count} notificações`}
        className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
        <Bell className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        {count > 0 && (
          <span data-testid="notif-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 max-h-[70vh] flex flex-col" data-testid="notif-menu">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-slate-100">Notificações</h4>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto flex-1">
              {count === 0 ? (
                <div className="p-8 text-center text-slate-500 dark:text-slate-400 text-sm">Nenhuma conta próxima do vencimento.</div>
              ) : (
                items.map((it) => {
                  const meta = LEVEL_META[it.notification_level] || LEVEL_META['5day'];
                  return (
                    <button key={it.id} onClick={() => { setOpen(false); navigate('/payable-receivable'); }}
                      className="w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40">
                      <div className="flex items-start gap-2">
                        <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${meta.tone.split(' ')[0]}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${meta.tone}`}>{meta.label}</span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">{formatDateBR(it.due_date)}</span>
                          </div>
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100 mt-0.5 truncate">
                            {it.description || '(sem descrição)'}
                          </div>
                          <div className={`text-sm font-semibold mt-0.5 ${it.type === 'payable' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {it.type === 'payable' ? '-' : '+'}{formatBRL(it.amount)}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
